import { useEffect, useState } from "react";

import { api, EventoTraza } from "../api/client";
import { Icon } from "../components/Icon";

/**
 * Trazabilidad: responde "¿qué pasó con este depósito?" cuando un cliente
 * pregunta, sin que nadie tenga que abrir la consola de AWS.
 *
 * Los eventos salen de CloudWatch, donde ya viven con el `execution_id`
 * adentro. Retención 90 días — la misma que tienen los grupos de log.
 */

type Pestana = "caso" | "log";

const ACTORES: { id: string; label: string; color: string }[] = [
  { id: "ia", label: "IA", color: "var(--info)" },
  { id: "persona", label: "Persona", color: "var(--accent)" },
  { id: "sistema", label: "Sistema", color: "var(--muted)" },
];

const COLOR_NIVEL: Record<string, string> = {
  ERROR: "var(--danger)",
  WARNING: "var(--warn)",
  INFO: "var(--muted)",
};

const COLS_LOG = "112px 66px 140px minmax(0,1fr) 24px";

/** Las últimas búsquedas, en el navegador de quien las hizo. Cuando un
 *  cliente reclama por segunda vez, volver a pegar el id es trabajo que ya
 *  se hizo una vez. */
const CLAVE_RECIENTES = "vouchers.traza.recientes";

function recientesGuardadas(): string[] {
  try {
    const crudo = localStorage.getItem(CLAVE_RECIENTES);
    return crudo ? (JSON.parse(crudo) as string[]).slice(0, 6) : [];
  } catch {
    return [];
  }
}

function guardarReciente(id: string): string[] {
  const lista = [id, ...recientesGuardadas().filter((x) => x !== id)].slice(0, 6);
  try {
    localStorage.setItem(CLAVE_RECIENTES, JSON.stringify(lista));
  } catch {
    /* modo privado: valen para esta sesión */
  }
  return lista;
}

/** Descarga un archivo armado en el navegador. */
function bajar(nombre: string, contenido: string, tipo: string) {
  const url = URL.createObjectURL(new Blob([contenido], { type: tipo }));
  const a = document.createElement("a");
  a.href = url;
  a.download = nombre;
  a.click();
  URL.revokeObjectURL(url);
}

const SALTO = String.fromCharCode(10);

export default function TrazabilidadPage() {
  const [pestana, setPestana] = useState<Pestana>("caso");

  return (
    <>
      <div className="filters" style={{ marginBottom: 14 }}>
        {([["caso", "Caso"], ["log", "Log del sistema"]] as const).map(([v, l]) => (
          <button key={v} className={"filter" + (pestana === v ? " on" : "")}
                  onClick={() => setPestana(v)}>
            {l}
          </button>
        ))}
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 11.5, color: "var(--faint)", alignSelf: "center" }}>
          Retención 90 días · solo administradores
        </span>
      </div>

      {pestana === "caso" ? <Caso /> : <LogSistema />}
    </>
  );
}

/** ── Pestaña Caso ──────────────────────────────────────────────────── */
function Caso() {
  const [id, setId] = useState("");
  const [buscado, setBuscado] = useState("");
  const [eventos, setEventos] = useState<EventoTraza[] | null>(null);
  const [cargando, setCargando] = useState(false);
  const [falla, setFalla] = useState<string | null>(null);
  const [actor, setActor] = useState("");
  const [abierto, setAbierto] = useState<number | null>(null);
  const [recientes, setRecientes] = useState<string[]>(recientesGuardadas);

  async function buscar(cual?: string) {
    const q = (cual ?? id).trim();
    if (!q) return;
    setId(q);
    setCargando(true); setFalla(null); setAbierto(null); setBuscado(q);
    try {
      const r = await api.getTraza(q);
      setEventos(r.items ?? []);
      // Solo se guarda lo que respondió: una búsqueda fallida no merece un
      // chip que invite a repetirla.
      setRecientes(guardarReciente(q));
    } catch (e: any) {
      setEventos(null);
      setFalla(e?.message || "No se pudo consultar la traza.");
    } finally {
      setCargando(false);
    }
  }

  const conteos = ACTORES.map((a) => ({
    ...a, n: (eventos ?? []).filter((e) => e.actor === a.id).length,
  }));
  const visibles = (eventos ?? []).filter((e) => !actor || e.actor === actor);

  return (
    <>
      <div className="card card-pad" style={{ marginBottom: 14 }}>
        <div className="rev-seccion">Buscar un depósito</div>
        <div className="rev-seccion-sub">
          Id de ejecución del depósito. Lo encuentras en la URL de su pantalla de revisión.
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
          <input value={id} placeholder="1c23ef37-dbfc-4a98-bfcb-7efedfc4873a"
                 style={{ flex: 1 }}
                 onChange={(e) => setId(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") buscar(); }} />
          <button className="btn primary" disabled={!id.trim() || cargando}
                  onClick={() => buscar()}>
            {cargando ? "Buscando..." : "Buscar"}
          </button>
        </div>

        {recientes.length > 0 && (
          <div className="traza-recientes">
            <span className="vistas-tit">Recientes</span>
            {recientes.map((r) => (
              <button key={r} className={"vista" + (buscado === r ? " on" : "")}
                      onClick={() => buscar(r)}>
                <span className="mono">{r.slice(0, 8)}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {falla && (
        <div className="empty falla">
          <div className="falla-titulo">No se pudo consultar la traza.</div>
          <div className="falla-detalle mono">{falla}</div>
          <div className="falla-nota">
            Que no se pueda consultar no significa que no haya pasado nada.
            Vuelve a intentar en unos segundos.
          </div>
          <button className="btn" onClick={() => buscar()}>Reintentar</button>
        </div>
      )}

      {eventos && !falla && eventos.length > 0 && (
        <Hitos eventos={eventos} id={buscado} />
      )}

      {eventos && !falla && (
        <div className="card">
          <div className="traza-cab">
            <div>
              <div className="rev-seccion">Qué pasó con este depósito</div>
              <div className="mono traza-id">{buscado}</div>
            </div>
            <div className="traza-actores">
              <button className={"filter" + (!actor ? " on" : "")}
                      onClick={() => setActor("")}>
                Todo <span className="n">{eventos.length}</span>
              </button>
              {conteos.map((a) => (
                <button key={a.id} disabled={!a.n}
                        className={"filter" + (actor === a.id ? " on" : "")}
                        onClick={() => setActor(a.id)}>
                  {a.label} <span className="n">{a.n}</span>
                </button>
              ))}
            </div>
          </div>

          {visibles.length === 0 ? (
            <div className="empty">
              {eventos.length === 0
                ? "No hay eventos registrados para ese depósito en los últimos 90 días."
                : `Ningún evento de «${actor}» en esta ejecución.`}
              {eventos.length > 0 && (
                <div style={{ marginTop: 10 }}>
                  <button className="btn" onClick={() => setActor("")}>
                    Ver todos los eventos
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="traza-linea">
              {visibles.map((e, n) => {
                const color = COLOR_NIVEL[e.nivel] ?? "var(--muted)";
                const importante = e.nivel === "ERROR" || e.nivel === "WARNING";
                const act = ACTORES.find((a) => a.id === e.actor);
                return (
                  <div key={n} className={"traza-ev" + (importante ? " alto" : "")}>
                    <div className="mono traza-hora">{hora(e.ts)}</div>
                    <div className="traza-punto"
                         style={{ background: color,
                                  width: importante ? 10 : 7,
                                  height: importante ? 10 : 7 }} />
                    <div style={{ minWidth: 0 }}>
                      <div className="traza-mensaje" style={{ color: importante ? color : undefined }}>
                        {e.mensaje}
                      </div>
                      <div className="traza-meta">
                        <span className="traza-actor" style={{ color: act?.color }}>
                          {act?.label ?? e.actor}
                        </span>
                        <span className="mono">{e.componente}</span>
                        {e.etapa && <span className="mono">{e.etapa}</span>}
                        <button className="traza-ver"
                                onClick={() => setAbierto(abierto === n ? null : n)}>
                          {abierto === n ? "ocultar" : "ver evento"}
                        </button>
                      </div>
                      {abierto === n && (
                        <pre className="traza-json">
                          {JSON.stringify(e.detalle, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </>
  );
}

/** ── Pestaña Log del sistema ───────────────────────────────────────── */
function LogSistema() {
  const [items, setItems] = useState<EventoTraza[]>([]);
  const [cargando, setCargando] = useState(true);
  const [falla, setFalla] = useState<string | null>(null);
  const [nivel, setNivel] = useState("");
  const [texto, setTexto] = useState("");
  const [horas, setHoras] = useState(24);
  const [abierto, setAbierto] = useState<number | null>(null);

  useEffect(() => {
    let vivo = true;
    setCargando(true); setFalla(null);
    api.getLogs({ horas, nivel, q: texto })
      .then((r) => { if (vivo) setItems(r.items ?? []); })
      .catch((e) => { if (vivo) setFalla(e?.message || "No se pudo cargar el log."); })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [horas, nivel, texto]);

  const errores = items.filter((e) => e.nivel === "ERROR").length;
  const avisos = items.filter((e) => e.nivel === "WARNING").length;

  return (
    <>
      <div className="filters">
        <div className="buscador">
          <Icon name="search" />
          <input value={texto} placeholder="Mensaje, componente o id de ejecución"
                 onChange={(e) => setTexto(e.target.value)} />
          {texto && (
            <button className="buscador-x" onClick={() => setTexto("")}
                    aria-label="Limpiar búsqueda">✕</button>
          )}
        </div>
        {[["", "Todos"], ["ERROR", "Errores"], ["WARNING", "Avisos"], ["INFO", "Info"]]
          .map(([v, l]) => (
            <button key={v} className={"filter" + (nivel === v ? " on" : "")}
                    onClick={() => setNivel(v)}>
              {l}
            </button>
          ))}
        <select value={horas} onChange={(e) => setHoras(Number(e.target.value))}>
          <option value={6}>Últimas 6 h</option>
          <option value={24}>Últimas 24 h</option>
          <option value={72}>Últimos 3 días</option>
          <option value={168}>Última semana</option>
        </select>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 11.5, color: "var(--muted)", alignSelf: "center" }}>
          {errores} errores · {avisos} avisos
        </span>
        <button className="btn" disabled={!items.length}
                onClick={() => bajar(
                  `log-${horas}h.log`,
                  items.map((e) => [e.ts, e.nivel, e.componente,
                                    e.execution_id || "-", e.mensaje].join(" · "))
                       .join(SALTO),
                  "text/plain;charset=utf-8")}>
          Descargar .log
        </button>
      </div>

      {falla ? (
        <div className="empty falla">
          <div className="falla-titulo">No se pudo cargar el log del sistema.</div>
          <div className="falla-detalle mono">{falla}</div>
        </div>
      ) : (
        <div className="card tabla-cola">
          <div className="thead" style={{ gridTemplateColumns: COLS_LOG }}>
            <div>Hora</div><div>Nivel</div><div>Componente</div>
            <div>Mensaje</div><div />
          </div>

          {cargando && <div className="empty">Consultando CloudWatch...</div>}

          {!cargando && items.length === 0 && (
            <div className="empty">Ningún evento con estos filtros.</div>
          )}

          {!cargando && items.map((e, n) => {
            const color = COLOR_NIVEL[e.nivel] ?? "var(--muted)";
            return (
              <div key={n}>
                <div className="trow fila-cola" style={{ gridTemplateColumns: COLS_LOG, cursor: "pointer" }}
                     onClick={() => setAbierto(abierto === n ? null : n)}>
                  <div className="mono" style={{ fontSize: 11.5, color: "var(--muted)" }}>
                    {hora(e.ts)}
                  </div>
                  <div>
                    <span className="badge" style={{ color, background: "transparent",
                                                     border: `1px solid ${color}` }}>
                      {e.nivel}
                    </span>
                  </div>
                  <div className="mono col-origen">{e.componente}</div>
                  <div className="col-falta" style={{ color }}>{e.mensaje}</div>
                  <div style={{ color: "var(--faint)" }}>{abierto === n ? "▾" : "▸"}</div>
                </div>
                {abierto === n && (
                  <div style={{ padding: "0 16px 14px" }}>
                    <pre className="traza-json">{JSON.stringify(e.detalle, null, 2)}</pre>
                    <div style={{ fontSize: 11.5, color: "var(--faint)", marginTop: 6 }}>
                      {e.execution_id
                        ? <>Depósito <span className="mono">{e.execution_id}</span></>
                        : "Evento de sistema · sin depósito asociado"}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {!cargando && items.length > 0 && (
            <div className="tabla-pie">
              Mostrando {items.length} · retención 90 días
            </div>
          )}
        </div>
      )}
    </>
  );
}

/**
 * Franja de hitos: las tres cosas que hay que poder decir de un depósito
 * antes de mirar la línea de tiempo.
 *
 * Todo sale de los eventos que ya se trajeron; no hay una consulta extra.
 */
function Hitos({ eventos, id }: { eventos: EventoTraza[]; id: string }) {
  const ts = eventos.map((e) => new Date(
    (e.ts ?? "").includes("Z") ? e.ts : `${e.ts}Z`).getTime())
    .filter((t) => !isNaN(t));
  const inicio = Math.min(...ts);
  const fin = Math.max(...ts);
  const segundos = ts.length > 1 ? (fin - inicio) / 1000 : 0;

  const deIA = eventos.filter((e) => e.actor === "ia").length;
  const persona = eventos.find((e) => e.actor === "persona");
  const problemas = eventos.filter((e) => e.nivel === "ERROR").length;
  const avisos = eventos.filter((e) => e.nivel === "WARNING").length;

  /** El texto que se le manda al cliente. Sin jerga, sin ids: nadie afuera
   *  del equipo sabe qué es un execution_id. */
  const paraElCliente = [
    `Sobre tu depósito:`,
    `· Lo recibimos el ${new Date(inicio).toLocaleString("es-PE", {
      day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit" })}.`,
    persona
      ? `· Lo revisó una persona de nuestro equipo.`
      : `· Lo procesó nuestro sistema de validación automática.`,
    problemas
      ? `· Encontramos un inconveniente y lo estamos revisando.`
      : `· No hubo inconvenientes en el procesamiento.`,
  ].join(SALTO);

  return (
    <div className="card traza-hitos">
      <div>
        <div className="rev-eyebrow">Desde que llegó</div>
        <div className="traza-hito-val tabular">
          {segundos ? `${segundos.toFixed(0)} s` : "—"}
        </div>
        <div className="traza-hito-pie">
          {new Date(inicio).toLocaleString("es-PE", {
            day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
        </div>
      </div>

      <div>
        <div className="rev-eyebrow">Lo automático</div>
        <div className="traza-hito-val tabular">{deIA}</div>
        <div className="traza-hito-pie">
          {deIA === 1 ? "paso del agente" : "pasos del agente"}
          {avisos > 0 && <> · {avisos} {avisos === 1 ? "aviso" : "avisos"}</>}
        </div>
      </div>

      <div>
        <div className="rev-eyebrow">Estado final</div>
        <div className="traza-hito-val"
             style={{ color: problemas ? "var(--danger)" : "var(--accent)",
                      fontSize: 15 }}>
          {problemas ? "Con errores" : persona ? "Decidido por una persona"
            : "Resuelto por el agente"}
        </div>
        <div className="traza-hito-pie">
          {persona ? persona.email || "alguien del equipo" : "sin intervención"}
        </div>
      </div>

      <div className="traza-hitos-acciones">
        <button className="btn"
                onClick={() => navigator.clipboard?.writeText(paraElCliente)}>
          Copiar respuesta para el cliente
        </button>
        <button className="btn ghost"
                onClick={() => bajar(`traza-${id.slice(0, 8)}.json`,
                                     JSON.stringify(eventos, null, 2),
                                     "application/json")}>
          Exportar traza (JSON)
        </button>
      </div>
    </div>
  );
}

function hora(ts: string | undefined) {
  if (!ts) return "—";
  const d = new Date(ts.includes("Z") || ts.includes("+") ? ts : `${ts}Z`);
  return isNaN(d.getTime()) ? ts : d.toLocaleString("es-PE", {
    day: "2-digit", month: "short", hour: "2-digit",
    minute: "2-digit", second: "2-digit",
  });
}
